import { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import api, { track } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  ArrowRight,
  ArrowLeft,
  HandsPraying,
  ShieldCheck,
  Sparkle,
  Quotes,
  CheckCircle,
} from "@phosphor-icons/react";

export default function TryItNowPage() {
  const [journey, setJourney] = useState(null);
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(true);
  const [done, setDone] = useState(false);
  const startedRef = useRef(false);

  useEffect(() => {
    api.get("/preview/journey")
      .then((r) => setJourney(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  // Fire 'preview_started' once the user actually opens the experience
  useEffect(() => {
    if (!startedRef.current) {
      startedRef.current = true;
      track("preview_started", {});
    }
  }, []);

  const next = () => {
    if (!journey) return;
    if (step < journey.steps.length - 1) {
      setStep(step + 1);
    } else {
      setDone(true);
      track("preview_completed", { journey: journey.id });
    }
  };
  const back = () => setStep(Math.max(0, step - 1));

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center text-[#8B7355] font-body text-sm">
        Loading…
      </div>
    );
  }
  if (!journey) {
    return (
      <div className="min-h-screen flex items-center justify-center px-6 text-center">
        <div>
          <p className="text-[#5C4033] font-body text-sm mb-4">Couldn't load the preview right now.</p>
          <Link to="/">
            <Button variant="outline" className="rounded-full">Back to home</Button>
          </Link>
        </div>
      </div>
    );
  }

  if (done) {
    return (
      <div className="min-h-screen flex items-center justify-center px-6 py-12 relative overflow-hidden" data-testid="try-done">
        <div className="spiritual-orb-connect w-96 h-96 top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 animate-orb opacity-50" />
        <div className="relative z-10 max-w-md w-full text-center">
          <div className="w-16 h-16 mx-auto rounded-full bg-[#FAF5EC] flex items-center justify-center mb-5">
            <CheckCircle size={36} weight="fill" className="text-[#C4A265]" />
          </div>
          <h1 className="font-heading text-3xl sm:text-4xl text-[#2D1B0E] mb-3 font-medium">
            {journey.after.headline}
          </h1>
          <p className="font-body text-[#5C4033] text-sm sm:text-base mb-8">
            {journey.after.sub}
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link to="/auth" onClick={() => track("preview_to_signup", {})}>
              <Button className="bg-[#2D1B0E] hover:bg-[#3D2B1F] text-white rounded-full px-7 py-5 text-base font-body w-full" data-testid="try-signup-btn">
                Create my account <ArrowRight size={16} className="ml-2" />
              </Button>
            </Link>
            <Link to="/">
              <Button variant="outline" className="border-[#D4C9BA] text-[#5C4033] hover:bg-white/50 rounded-full px-7 py-5 font-body w-full">
                Back to landing
              </Button>
            </Link>
          </div>
          <p className="text-[#B8A088] text-xs font-body mt-6 max-w-sm mx-auto">
            Free to start. Scripture-grounded. An extension of the church—not a replacement.
          </p>
        </div>
      </div>
    );
  }

  const cur = journey.steps[step];
  const total = journey.steps.length;

  return (
    <div className="min-h-screen flex flex-col items-center justify-center px-6 py-10 relative" data-testid="try-page">
      <div className="spiritual-orb-connect w-80 h-80 top-1/3 left-1/2 -translate-x-1/2 -translate-y-1/2 animate-orb opacity-40" />

      <div className="relative z-10 w-full max-w-lg">
        <Link to="/" className="inline-flex items-center gap-1 text-[#8B7355] text-xs font-body mb-6">
          <ArrowLeft size={14} /> Back
        </Link>

        <div className="text-center mb-6">
          <p className="text-[#C4A265] text-[10px] uppercase tracking-[0.25em] font-body font-medium mb-2 inline-flex items-center gap-1">
            <Sparkle size={10} weight="fill" /> Try Voice now · No account needed
          </p>
          <h1 className="font-heading text-2xl sm:text-3xl text-[#2D1B0E] font-medium">
            {journey.name}
          </h1>
          <p className="font-body text-[#8B7355] text-sm mt-1">{journey.description}</p>
        </div>

        {/* Step indicators */}
        <div className="flex items-center justify-center gap-2 mb-6">
          {journey.steps.map((_, i) => (
            <div
              key={i}
              className={`h-1.5 rounded-full transition-all ${i === step ? "bg-[#C4A265] w-8" : i < step ? "bg-[#C4A265]/50 w-4" : "bg-[#D4C9BA] w-4"}`}
            />
          ))}
          <span className="text-[#8B7355] text-xs font-body ml-2">{step + 1}/{total}</span>
        </div>

        {/* Card */}
        <div className="glass-card-strong p-6 sm:p-8 mb-6">
          <div className="flex items-center gap-2 mb-3">
            <HandsPraying size={18} weight="duotone" className="text-[#C4A265]" />
            <span className="text-[#C4A265] text-[10px] uppercase tracking-[0.2em] font-body font-medium">
              Step {step + 1} · {cur.title}
            </span>
          </div>
          {cur.scripture && (
            <div className="mb-4 pb-4 border-b border-[#E8DFD3]">
              <Quotes size={16} weight="fill" className="text-[#C4A265] mb-1" />
              <p className="font-heading text-sm text-[#2D1B0E] italic leading-relaxed">{cur.scripture}</p>
            </div>
          )}
          <p className="font-body text-[#5C4033] leading-relaxed text-base">
            {cur.text}
          </p>
        </div>

        <div className="flex items-center justify-between gap-3">
          {step > 0 ? (
            <Button variant="outline" onClick={back} className="border-[#D4C9BA] text-[#5C4033] rounded-full px-5 font-body" data-testid="try-back-btn">
              <ArrowLeft size={14} className="mr-1" /> Back
            </Button>
          ) : <span />}
          <Button onClick={next} className="bg-[#2D1B0E] hover:bg-[#3D2B1F] text-white rounded-full px-7 py-5 font-body" data-testid="try-next-btn">
            {step < total - 1 ? "Continue" : "Amen — Finish"} <ArrowRight size={14} className="ml-1" />
          </Button>
        </div>

        <div className="flex items-center justify-center gap-4 mt-6 text-[#B8A088] text-[10px] font-body">
          <span className="flex items-center gap-1">
            <ShieldCheck size={11} weight="fill" className="text-[#C4A265]" /> Scripture-grounded
          </span>
          <span>·</span>
          <span>No tracking unless you choose to sign up</span>
        </div>
      </div>
    </div>
  );
}
